import { createContext, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdentity, setActiveIdentity] = useState(null); // identità reale del browser se diversa da questa scheda
  const tabTokenRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("gc_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => { tabTokenRef.current = token; setUser(res.data); })
      .catch(() => localStorage.removeItem("gc_token"))
      .finally(() => setLoading(false));
  }, []);

  // Rileva quando un altro account effettua l'accesso/logout in un'altra scheda dello stesso browser
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "gc_token") return;
      const nv = e.newValue;
      if (!nv) {
        if (tabTokenRef.current) setActiveIdentity({ loggedOut: true });
        return;
      }
      if (nv !== tabTokenRef.current) {
        api.get("/auth/me").then((r) => setActiveIdentity(r.data)).catch(() => setActiveIdentity({ unknown: true }));
      } else {
        setActiveIdentity(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("gc_token", data.token);
    tabTokenRef.current = data.token;
    setActiveIdentity(null);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("gc_token", data.token);
    tabTokenRef.current = data.token;
    setActiveIdentity(null);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("gc_token");
    tabTokenRef.current = null;
    setActiveIdentity(null);
    setUser(null);
  };

  const mismatch = !!activeIdentity && (activeIdentity.loggedOut || activeIdentity.unknown || activeIdentity.id !== user?.id);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {mismatch && <SessionMismatchBanner current={user} active={activeIdentity} />}
      {children}
    </AuthContext.Provider>
  );
}

const ROLE_LABEL = { admin: "Amministratore", direttore: "Direttore di Giuria", judge: "Giudice", segreteria: "Segreteria", public: "Pubblico" };

function SessionMismatchBanner({ current, active }) {
  const label = (u) => {
    if (!u) return "questo account";
    const roleLbl = ROLE_LABEL[u.role] || u.role;
    return u.name && u.name !== roleLbl ? `${u.name}${u.role ? ` (${roleLbl})` : ""}` : (u.name || roleLbl);
  };
  let message;
  if (active?.loggedOut) {
    message = "La sessione è stata chiusa in un'altra scheda di questo browser. Ricarica per aggiornare.";
  } else if (active?.unknown) {
    message = "In questo browser è cambiato l'account attivo. Le azioni di questa scheda useranno il nuovo account. Ricarica per allinearla.";
  } else {
    message = `Attenzione: in questo browser ora è attivo ${label(active)}. Questa scheda mostra ancora ${label(current)}, ma i voti verranno salvati come ${active?.name || "il nuovo account"}. Per usare più account insieme, apri ognuno su un dispositivo o una finestra separata.`;
  }
  return (
    <div data-testid="session-mismatch-banner" className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-[#020617] px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium shadow-lg">
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span className="text-center">{message}</span>
      <button data-testid="session-mismatch-reload" onClick={() => window.location.reload()} className="shrink-0 rounded-md bg-[#020617] text-amber-300 px-3 py-1 text-xs font-semibold hover:bg-[#0F172A] transition-colors">Ricarica</button>
    </div>
  );
}

export const useAuth = () => useContext(AuthContext);
